/**
 * /r/:token — public, anonymous read-only report.
 *
 * Renders the same find-and-fix issue list a logged-in user would see at
 * /domains/:id, but with NO auth and a stripped header. The entire surface is
 * driven by `GET /public/reports/:token`, which we hit without a Bearer
 * header — the token IS the credential.
 *
 * Errors:
 *   - 404 → "Report not found." (URL never existed or was malformed)
 *   - 410 → "This report has been revoked." (org turned the link off)
 */
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { Lock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { IssueList, collectIssues, type ScanRow } from '@/components/IssueList';
import { DnssecCard } from '@/components/DnssecCard';
import { BimiCard } from '@/components/BimiCard';
import { PublicHeader } from '@/components/PublicHeader';
import { cn } from '@/lib/utils';
import { scoreSubtitle } from '@/lib/score';

interface PublicReport {
  domain: { name: string };
  branding: {
    orgName: string;
    brandLogoUrl: string | null;
    brandColor: string | null;
  };
  latestScan: ScanRow | null;
  sharedAt: string;
  generatedAt: string;
}

interface PublicReportError {
  status: number;
  code: string;
  message: string;
}

export function PublicReportPage() {
  const { token } = useParams<{ token: string }>();

  const report = useQuery<PublicReport, PublicReportError>({
    queryKey: ['public-report', token],
    enabled: !!token,
    retry: false,
    queryFn: async () => {
      // Hit the API directly — no Bearer token. The api client always attaches
      // auth, so we bypass it here.
      const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';
      const res = await fetch(`${base}/public/reports/${token}`, {
        headers: { 'content-type': 'application/json' },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw {
          status: res.status,
          code: body?.error?.code ?? 'UNKNOWN',
          message: body?.error?.message ?? `HTTP ${res.status}`,
        } satisfies PublicReportError;
      }
      return body as PublicReport;
    },
  });

  if (report.isLoading) {
    return (
      <PublicShell>
        <Card>
          <CardContent className="p-8 text-sm text-muted-foreground">Loading report…</CardContent>
        </Card>
      </PublicShell>
    );
  }

  if (report.error) {
    const isRevoked = report.error.status === 410;
    return (
      <PublicShell>
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <Lock className="h-8 w-8 text-muted-foreground" />
            <h1 className="text-lg font-semibold">
              {isRevoked ? 'This report link has been revoked.' : 'Report not found.'}
            </h1>
            <p className="max-w-md text-sm text-muted-foreground">
              {isRevoked
                ? 'The owner turned this share link off. Ask them for a new one if you need access.'
                : "This URL doesn't match any published report. Double-check the link, or ask the owner to send a fresh one."}
            </p>
          </CardContent>
        </Card>
      </PublicShell>
    );
  }

  const data = report.data!;
  const issues = collectIssues(data.latestScan);

  return (
    <>
      <Helmet>
        <title>{data.domain.name} — InboxGuard report</title>
      </Helmet>
      <PublicShell>
        <div className="mb-6">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Email deliverability report
          </p>
          <h1 className="mt-1 font-mono text-3xl font-bold tracking-tight">
            {data.domain.name}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Shared by {data.branding.orgName} · generated{' '}
            {new Date(data.generatedAt).toLocaleString()}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Card className="md:col-span-1">
            <CardContent className="p-6">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Deliverability score
              </p>
              <div
                className={cn(
                  'mt-2 font-mono text-5xl font-bold',
                  scoreTextClass(data.latestScan?.score ?? null),
                )}
              >
                {data.latestScan?.score ?? '—'}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {scoreSubtitle(data.latestScan?.score ?? null, data.latestScan?.score_breakdown) ?? 'out of 100'}
              </p>
            </CardContent>
          </Card>
          <Card className="md:col-span-2">
            <CardContent className="p-6">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Summary</p>
              <p className="mt-2 text-sm">
                {summaryFor(issues.length, data.latestScan?.score ?? null)}
              </p>
              <p className="mt-3 text-xs text-muted-foreground">
                Last scanned{' '}
                {data.latestScan
                  ? new Date(data.latestScan.run_at).toLocaleString()
                  : 'never'}
                .
              </p>
            </CardContent>
          </Card>
        </div>

        <h2 className="mt-10 text-xl font-semibold">Issues to fix</h2>
        <div className="mt-3">
          <IssueList issues={issues} domainName={data.domain.name} />
        </div>

        {/* DNSSEC/DANE and BIMI/VMC detail — render only when present. These
            are typically absent on the anonymous tier, so the public report
            usually omits them entirely. */}
        <DnssecCard dnssec={data.latestScan?.dnssec} />
        <BimiCard bimi={data.latestScan?.bimi} />

        <div className="mt-12 border-t pt-6 text-center text-xs text-muted-foreground">
          Generated by{' '}
          <a href="/" className="font-medium text-primary hover:underline">
            InboxGuard
          </a>{' '}
          — DMARC, SPF, DKIM, and blocklist monitoring.
        </div>
      </PublicShell>
    </>
  );
}

function PublicShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <main className="mx-auto max-w-4xl px-4 py-10">{children}</main>
    </div>
  );
}

function summaryFor(issueCount: number, score: number | null): string {
  if (score === null) {
    return 'No scan data yet for this domain.';
  }
  if (issueCount === 0) {
    return 'All deliverability checks are passing. No action required.';
  }
  if (score >= 75) {
    return `Domain is in good shape but has ${issueCount} item(s) worth tightening up. Each is expandable below with copy-pasteable DNS records.`;
  }
  if (score >= 50) {
    return `Domain has ${issueCount} deliverability issue(s) that need attention to avoid spam folders. Each is expandable below with the fix.`;
  }
  return `Domain has ${issueCount} critical deliverability issue(s). Mail is likely being blocked or routed to spam. Each is expandable below with step-by-step fixes.`;
}

function scoreTextClass(score: number | null): string {
  if (score === null) return 'text-muted-foreground';
  if (score >= 75) return 'text-success';
  if (score >= 50) return 'text-warning';
  return 'text-destructive';
}

export default PublicReportPage;
